import { useEffect, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Link, Navigate, Outlet, useMatch, useParams, useOutletContext } from "react-router-dom";
import { Alert, ApiError, Spinner, cn, type PartnerOrgProfileResponse } from "@dearbill/shared";
import { useApi } from "../auth/useApi";
import { usePartnerSession } from "../auth/usePartnerSession";
import { buildOrgStatusBanner } from "../routes/orgStatusBanner";
import { OrgContextProvider } from "./OrgContext";
import layoutStyles from "./WorkspaceLayout.module.css";
import type { AuthenticatedLayoutContext } from "./AuthenticatedLayout";
import { OrgTabBar } from "./OrgTabBar";
import { PharmacyTourButton, PharmacyWalkthroughProvider } from "../components/PharmacyWalkthrough";
import { PharmacyOnboardingProvider } from "../components/onboarding/PharmacyOnboardingProvider";
import {
  PharmacyOnboardingLink,
  PharmacyOnboardingResume,
} from "../components/onboarding/PharmacyOnboardingLinks";

/** The disabled-org notice. "Contact Us" in it is plain text, not a link. */
const ORG_DISABLED_MESSAGE =
  "This organization has been disabled by DearBill. Contact Us for more information.";

/**
 * True when a profile-fetch failure is the admin-disabled 403, whose parsed
 * `ApiError.body` is `{ detail: { code: "org_disabled" } }`. Any other 403 stays
 * on the access-denied branch.
 */
function isOrgDisabledError(err: unknown): boolean {
  if (!(err instanceof ApiError) || err.status !== 403) return false;
  const body = err.body;
  if (typeof body !== "object" || body === null || !("detail" in body)) return false;
  const detail = body.detail;
  if (typeof detail !== "object" || detail === null || !("code" in detail)) return false;
  return detail.code === "org_disabled";
}

/**
 * Shell for the org page (`/orgs/:orgId/*`): the org header above the
 * `OrgTabBar`, the status banner and the routed content.
 *
 * Owns the one `GET /api/partner/orgs/{orgId}/profile` fetch (which carries the
 * caller's `role`, the org `name`, computed `status` and `blockers`) and
 * publishes it through `OrgContext`, so the tab bar and the child routes read it
 * without prop drilling. A successful profile save lifts the server's updated
 * response back through the context's `setRes`, so the header and banner
 * re-render.
 *
 * The shell renders its chrome only once that fetch resolves. The global
 * `TopBar` owns identity, sign-out and contact; the org header is org-scoped.
 * `RequireOrgAdmin` also guards the Team route, because the tab bar only hides
 * its tab.
 */
export function OrgShell(): ReactNode {
  const api = useApi();
  const layout = useOutletContext<AuthenticatedLayoutContext | undefined>();
  const { session } = usePartnerSession();
  const { orgId } = useParams<{ orgId: string }>();
  // The banner's link goes to the Organization tab, so it is hidden there; the
  // message still shows.
  const onProfilePage = useMatch("/orgs/:orgId/organization") !== null;
  const onIntroduction = useMatch("/orgs/:orgId/get-started") !== null;

  const [res, setRes] = useState<PartnerOrgProfileResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [orgDisabled, setOrgDisabled] = useState(false);

  useEffect(() => {
    if (!session || !orgId) return;
    let cancelled = false;
    setRes(null);
    setLoadError(null);
    setAccessDenied(false);
    setOrgDisabled(false);
    api<PartnerOrgProfileResponse>(`/api/partner/orgs/${orgId}/profile`)
      .then((data) => {
        if (!cancelled) setRes(data);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // A 401 already cleared the session in `useApi`, and the redirect takes over.
        if (err instanceof ApiError && err.status === 401) return;
        // The admin-disabled 403 gets its own notice, so it is checked before the
        // generic 403/404 branch. Direct URL navigation lands here too, so the
        // notice shows even with a stale cached `/me`.
        if (isOrgDisabledError(err)) {
          setOrgDisabled(true);
          return;
        }
        if (err instanceof ApiError && (err.status === 403 || err.status === 404)) {
          setAccessDenied(true);
          return;
        }
        setLoadError("We couldn't load this organization. Please try again.");
      });
    return () => {
      cancelled = true;
    };
  }, [api, session, orgId]);

  if (!session) return <Navigate to="/login" replace />;

  if (orgDisabled) {
    return (
      <CenteredNotice>
        <Alert tone="error" title={ORG_DISABLED_MESSAGE} />
      </CenteredNotice>
    );
  }
  if (accessDenied) {
    return (
      <CenteredNotice>
        <Alert tone="error" title="You don't have access to this organization.">
          Use the DearBill logo at the top of the page to go back to your organizations. If you
          think you should have access, ask your organization&apos;s admin to invite you.
        </Alert>
      </CenteredNotice>
    );
  }
  if (loadError) {
    return (
      <CenteredNotice>
        <Alert tone="error" title={loadError}>
          Reload the page to try again. If it keeps happening, use “Need help? Call” at the top of
          the page.
        </Alert>
      </CenteredNotice>
    );
  }
  if (res === null || !orgId || res.orgId !== orgId) {
    return (
      <CenteredNotice>
        <div className="text-slate-teal flex items-center gap-3">
          <Spinner /> <span>Loading organization…</span>
        </div>
      </CenteredNotice>
    );
  }

  const banner = buildOrgStatusBanner(res.status, res.blockers, res.underReview);

  return (
    <OrgContextProvider
      value={{
        orgId,
        res,
        setRes,
        isOrgAdmin: res.role === "org_admin",
      }}
    >
      <PharmacyOnboardingProvider key={orgId}>
        <PharmacyWalkthroughProvider>
          {layout?.tourSlot && createPortal(<PharmacyTourButton />, layout.tourSlot)}
          <div
            className={cn(
              "bg-background flex min-h-full min-w-0 flex-col",
              !onIntroduction && layoutStyles.fixedOrganization,
            )}
          >
            <OrgHeader res={res} />
            <OrgTabBar />
            <PharmacyOnboardingResume />
            <div className="flex min-h-0 min-w-0 flex-1 flex-col">
              {banner && (
                <div
                  role="status"
                  className="border-warning-border bg-lemon text-ochre border-b px-3 py-3 [overflow-wrap:anywhere] sm:px-8"
                >
                  {banner.message}
                  {/* The link text names its destination, so it reads correctly out of context. */}
                  {banner.linkToProfile && !onProfilePage && (
                    <>
                      {" "}
                      <Link
                        to={`/orgs/${orgId}/organization`}
                        className="text-ochre hover:text-midnight font-semibold underline"
                      >
                        go to your Organization Profile
                      </Link>
                    </>
                  )}
                </div>
              )}
              <div className={cn("min-w-0 flex-1", !onIntroduction && layoutStyles.viewport)}>
                <div className="max-w-page mx-auto w-full px-3 pb-10 pt-7 sm:px-7">
                  <Outlet />
                </div>
              </div>
            </div>
          </div>
        </PharmacyWalkthroughProvider>
      </PharmacyOnboardingProvider>
    </OrgContextProvider>
  );
}

/**
 * Org header between the global top bar and the tab bar: the org name over its
 * type and verification state, and a "Switch organization" link to the org list
 * (Figma dR8cJ6hRSW5eERToo64boe, node 2340:38).
 *
 * The design labels the name SemiBold, but Libre Caslon Text ships only 400
 * and 700 faces, so `font-semibold` resolves to the real 700 face rather than
 * a synthesized weight. The design's "N people on the account" segment is
 * omitted: the partner profile response carries no member count, and the
 * members list is org-admin-only, so regular members couldn't see it.
 */
function OrgHeader({ res }: { res: PartnerOrgProfileResponse }): ReactNode {
  const subtitle = [
    res.profile.organizationType,
    res.status === "active" ? "Active" : res.underReview ? "Review in progress" : "Setup needed",
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="bg-white">
      <div className="max-w-page mx-auto flex w-full flex-wrap items-center justify-between gap-4 px-3 pb-5 pt-[22px] sm:px-7">
        <div className="min-w-0 flex-1 basis-64">
          <h1
            className="text-foreground break-words font-serif text-[26px] font-semibold leading-8"
            title={res.name}
          >
            {res.name}
          </h1>
          <p className="text-slate-teal mt-1 text-sm leading-5">{subtitle}</p>
        </div>
        <div className="flex min-w-0 max-w-full flex-wrap justify-start gap-2 sm:justify-end">
          <PharmacyOnboardingLink />
          {/* A Link styled as a button, because the shared Button renders only <button>. */}
          <Link
            to="/"
            className="border-haze text-foreground hover:bg-ice inline-flex min-h-11 max-w-full items-center justify-center whitespace-normal rounded-lg border bg-white px-4 py-3 text-center text-base font-semibold leading-[1.5] transition [overflow-wrap:anywhere]"
          >
            Switch organization
          </Link>
        </div>
      </div>
    </div>
  );
}

/** Wrapper for the shell's loading and error states. */
function CenteredNotice({ children }: { children: ReactNode }): ReactNode {
  return (
    <div className="bg-background min-h-screen">
      <div className="mx-auto max-w-3xl px-6 py-10">{children}</div>
    </div>
  );
}
